import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { getSharedBinaryAtlas } from '../../rendering/binary/sharedBinaryResources.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import { RoadVehicle } from './RoadVehicle.js';
import { debugLog } from '../../debug/debugLog.js';

/**
 * Red binary vehicles driving on procedural road grid strips near the player.
 */
export class RoadVehicleTraffic {
  /** @param {{ config?: typeof CONFIG.roadVehicles; cityConfig?: typeof CONFIG.city }} [options] */
  constructor({
    config = CONFIG.roadVehicles,
    cityConfig = CONFIG.city,
  } = {}) {
    this.config = config;
    this.cityConfig = cityConfig;
    this.group = new THREE.Group();
    this.group.name = 'RoadVehicleTraffic';

    /** @type {RoadVehicle[]} */
    this._vehicles = [];
    this._material = null;
    this._lastAnchor = { x: NaN, z: NaN };
    this._cellBase = 1_350_000;

    if (config.enabled) {
      this._build();
    }
  }

  _build() {
    const atlas = getSharedBinaryAtlas();
    this._material = new BinaryMaterial({
      atlas,
      palette: 'accent',
      overrides: {
        glowStrength: CONFIG.luminance.vehicleGlowStrength,
        flickerStrength: 0.12,
        scrollSpeed: 4.1,
        pulseSpeed: 1.45,
      },
    });

    let cellOffset = this._cellBase;
    let maxColumns = 8;
    const count = Math.max(0, Math.floor(this.config.count));

    for (let i = 0; i < count; i += 1) {
      const vehicle = new RoadVehicle({
        material: this._material,
        seed: this.config.seed + i * 61,
        cellIdBase: cellOffset,
        config: this.config,
        cityConfig: this.cityConfig,
        index: i,
      });
      cellOffset += vehicle.cellsUsed;
      maxColumns = Math.max(maxColumns, vehicle.maxColumns);
      this._vehicles.push(vehicle);
      this.group.add(vehicle.root);
    }

    this._material.setColumns(maxColumns);
    debugLog('[RoadVehicleTraffic]', {
      vehicles: this._vehicles.length,
      cellBase: cellOffset,
    });
  }

  /**
   * @param {number} delta
   * @param {{ x: number; y: number; z: number }} playerPosition
   */
  update(delta, playerPosition) {
    if (!this.config.enabled || !this._material) return;

    if (!CONFIG.graphics?.staticBinaryGlyphs) {
      this._material.update(delta);
    }

    const { x, z } = playerPosition;
    const moveThreshold = this.config.recenterDistance;
    if (
      Number.isNaN(this._lastAnchor.x) ||
      Math.hypot(x - this._lastAnchor.x, z - this._lastAnchor.z) > moveThreshold
    ) {
      this._lastAnchor.x = x;
      this._lastAnchor.z = z;
    }

    const anchorX = this._lastAnchor.x;
    const anchorZ = this._lastAnchor.z;
    for (const vehicle of this._vehicles) {
      vehicle.update(delta, anchorX, anchorZ);
    }
  }

  /**
   * @param {{ x: number; y: number; z: number }} listenerPosition
   * @param {number} listenYaw
   * @param {number} [maxDistance]
   */
  getAudioSnapshot(listenerPosition, listenYaw, maxDistance) {
    const max =
      maxDistance ?? CONFIG.audio?.vehicles?.maxHearDistance ?? 48;
    const maxSq = max * max;
    let nearestSq = Infinity;
    let nearestDx = 0;
    let nearestDz = 0;
    let audibleCount = 0;
    let nearestSpeed = 0;

    for (const vehicle of this._vehicles) {
      const wx = vehicle.root.position.x;
      const wy = vehicle.root.position.y;
      const wz = vehicle.root.position.z;
      const dx = wx - listenerPosition.x;
      const dy = wy - listenerPosition.y;
      const dz = wz - listenerPosition.z;
      const dSq = dx * dx + dy * dy + dz * dz;
      if (dSq <= maxSq) audibleCount += 1;
      if (dSq < nearestSq) {
        nearestSq = dSq;
        nearestDx = dx;
        nearestDz = dz;
        nearestSpeed = vehicle.speed;
      }
    }

    if (!Number.isFinite(nearestSq) || nearestSq === Infinity) {
      return {
        distance: Infinity,
        pan: 0,
        audibleCount: 0,
        nearestSpeed: 0,
      };
    }

    const worldAngle = Math.atan2(nearestDx, nearestDz);
    const pan = Math.sin(worldAngle - listenYaw);
    return {
      distance: Math.sqrt(nearestSq),
      pan,
      audibleCount,
      nearestSpeed,
    };
  }

  /** @param {number} boost */
  setEventBoost(boost) {
    this._material?.setEventBoost(boost);
  }

  dispose() {
    for (const vehicle of this._vehicles) vehicle.dispose();
    this._vehicles = [];
    this._material?.dispose();
    this._material = null;
    this.group.clear();
  }
}
